import React, { useRef, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { 
  AllCommunityModule, 
  ModuleRegistry,
  RowSelectionModule,
  themeBalham,
  colorSchemeDarkBlue,
  colorSchemeLight
} from 'ag-grid-community';
import { useTheme } from '@mui/material';

// 모듈 등록 (한 번만 실행되도록 컴포넌트 외부에 위치)
ModuleRegistry.registerModules([AllCommunityModule, RowSelectionModule]);

// 한국어 로컬라이제이션 설정
const koKR = {
  // 공통
  loadingOoo: '로딩 중...',
  noRowsToShow: '표시할 데이터가 없습니다.',
  
  // 페이지네이션
  page: '페이지',
  more: '더 보기',
  to: '에서',
  of: '중',
  next: '다음',
  last: '마지막',
  first: '처음',
  previous: '이전',
  
  // 필터링
  filterOoo: '필터...',
  equals: '같음',
  notEqual: '같지 않음',
  lessThan: '작음',
  greaterThan: '큼',
  lessThanOrEqual: '작거나 같음',
  greaterThanOrEqual: '크거나 같음',
  inRange: '범위 내',
  contains: '포함',
  notContains: '포함하지 않음',
  startsWith: '시작',
  endsWith: '끝',
  blank: '비어있음',
  notBlank: '비어있지 않음',
  
  // 선택
  selectAll: '모두 선택',
  searchOoo: '검색...',
  blanks: '비어있음',
  
  // 그리드 메뉴
  pinColumn: '열 고정',
  pinLeft: '왼쪽 고정',
  pinRight: '오른쪽 고정',
  noPin: '고정 해제',
  autosizeThiscolumn: '이 열 자동 크기 조정',
  autosizeAllColumns: '모든 열 자동 크기 조정',
  resetColumns: '열 초기화',
  
  // 기타
  copy: '복사',
  copyWithHeaders: '헤더와 함께 복사',
  ctrlC: 'Ctrl+C',
  paste: '붙여넣기',
  ctrlV: 'Ctrl+V',
  export: '내보내기',
  csvExport: 'CSV 내보내기',
  excelExport: 'Excel 내보내기',
  
  // 그룹
  group: '그룹',
  ungroup: '그룹 해제',
  
  // 행 그룹
  rowGroupColumnsEmptyMessage: '그룹화할 열을 여기로 드래그하세요'
};

const MasterAgGrid = ({
  gridRef,
  rowData,
  columnDefs,
  onSelectionChanged,
  onGridReady,
  onRowDoubleClicked,
  pageSize = 100,
  paginationPageSizeSelector = [20, 50, 100],
  customDefaultColDef = {},
  loading = false,
  isRowSelectable, // 외부에서 전달 받아 rowSelection 옵션에 적용
  ...props
}) => {
  const theme = useTheme();

  // 그리드 테마 설정
  const gridTheme = useMemo(() => {
    return theme.palette.mode === 'dark' 
      ? themeBalham.withPart(colorSchemeDarkBlue)
      : themeBalham.withPart(colorSchemeLight);
  }, [theme.palette.mode]);

  // 행 선택 설정 (v33: rowSelection 옵션 객체 사용)
  const rowSelection = useMemo(() => {
    return {
      mode: 'multiRow',
      selectAll: 'filtered',
      enableClickSelection: false,
      isRowSelectable,
    };
  }, [isRowSelectable]);

  // 기본 열 정의
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      editable: false, // 직접 편집 비활성화 (다이얼로그로 대체)
      floatingFilter: true,
      minWidth: 100,
      headerClass: 'ag-header-center', // 헤더 중앙 정렬
      cellClass: 'ag-cell-center', // 셀 중앙 정렬
      ...customDefaultColDef
    };
  }, [customDefaultColDef]);

  // 테마 변경 시 그리드 리프레시
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshHeader();
      gridRef.current.api.redrawRows();
    }
  }, [theme.palette.mode, gridRef]);

  // 그리드 준비 완료 기본 핸들러
  const handleGridReady = (params) => {
    // 모든 컬럼 자동 크기 조정
    setTimeout(() => {
      params.api.autoSizeAllColumns();
    }, 0);
    
    if (onGridReady) onGridReady(params);
  };

  // 데이터 변경 시 컬럼 크기 자동 조정
  useEffect(() => {
    if (gridRef.current && gridRef.current.api && rowData.length > 0) {
      // 데이터가 로드된 후 약간의 지연을 두고 자동 크기 조정 실행
      const timer = setTimeout(() => {
        gridRef.current.api.sizeColumnsToFit();
        gridRef.current.api.autoSizeAllColumns();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [rowData, gridRef]);

  // 윈도우 크기 변경 시 그리드 크기 조정
  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [gridRef]);

  return (
    <div style={{ 
      height: '100%', 
      width: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <style>
        {`
          /* 체크박스 헤더 라벨 숨김 */
          .ag-header-row .ag-header-cell:first-child .ag-header-cell-comp-wrapper {
            display: none !important;
          }
          .ag-header-cell:first-child {
            justify-content: center !important;
          }

          /* 체크박스 셀 중앙 정렬 */
          [col-id="ag-Grid-SelectionColumn"] {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
          }

          /* 체크박스 마진 제거 */
          .ag-selection-checkbox {
            margin: 0 !important;
          }

          /* 마지막 컬럼 셀에 오른쪽 테두리 추가 */
          .ag-cell.ag-column-last {
            border-right: 1px solid var(--ag-border-color) !important;
          }
          

          .ag-cell-center {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
          }
          
          .ag-header-center .ag-header-cell-label {
            justify-content: center !important;
          }

          .ag-cell-focus,.ag-cell-no-focus{
            border:none !important;
          }

          /* 액션(편집) 컬럼 수직/수평 중앙 정렬 보정 */
          .ag-cell[col-id="actions"] .ag-cell-wrapper {
            height: 100% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .ag-cell[col-id="actions"] .ag-cell-value {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 100% !important;
            height: 100% !important;
          }
        `}
      </style>
      <AgGridReact
        theme={gridTheme}
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection={rowSelection}
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginationPageSizeSelector}
        onSelectionChanged={onSelectionChanged}
        onRowDoubleClicked={onRowDoubleClicked}
        overlayNoRowsTemplate="표시할 데이터가 없습니다."
        domLayout="normal"
        onGridReady={handleGridReady}
        stopEditingWhenCellsLoseFocus={true}
        enableCellTextSelection={true}
        ensureDomOrder={true}
        localeText={koKR}
        {...props}
      />
    </div>
  );
};

export default MasterAgGrid; 